import React from 'react'
import {
  Box, Typography, Link, useTheme 
} from '@mui/material'

import { PortableText } from './PortableText'
import { SectionDivider } from './SectionDivider'

import { rudderslabTrackOnClick } from '~/utils/common'
import { ThemeProvider } from '~/theme'

export type MiddleBannerProps = {
  title?: string
  small_title?: string
  smalldescription?: object
  addbuttons?: {
    _key: string
    btntext: string
    btnlink: string
    btnexternallink: boolean
    btnhiglight: boolean
  }[]
  smallerTitle?: boolean
  isLightTheme?: boolean
}

export const MiddleBanner = ({
  title,
  small_title: smallTitle,
  smalldescription,
  addbuttons,
  smallerTitle,
  isLightTheme,
}: MiddleBannerProps) => {
  const { palette } = useTheme()

  return (
    <ThemeProvider mode='dark'>
      <SectionDivider />

      <Box
        component='section'
        sx={{
          bgcolor: 'background.default',
          pt: { xs: 5, desktop: 12 },
          pb: { xs: 0, desktop: 8 },
          position: 'relative',
          mb: -0.5,
        }}>
        <Box
          sx={{
            maxWidth: 1152,
            mx: 'auto',
          }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              flexDirection: { xs: 'column', tablet: 'row' },
              alignItems: 'center',
              px: { xs: 3.5, desktop: 0 },
            }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                width: { xs: '100%', tablet: '50%', desktop: '40%' },
                alignItems: { mobile: 'flex-start' },
              }}>
              <Box
                component='img'
                src={`/images/${
                  isLightTheme ? 'Arrow-midnight-blue' : 'Arrow-sg'
                }.svg`}
                alt='Get Started Image'
                sx={{
                  width: { xs: 40, tablet: 64 },
                  pt: { laptop: -2 },
                }}
              />

              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: { xs: 'column', desktop: 'row' },
                  pl: { xs: 1.5, laptop: 6 },
                }}>
                {smallTitle && (
                  <Typography
                    variant='subtitle2'
                    sx={{
                      mb: 2,
                      fontSize: { xs: 14, tablet: 24 },
                      fontWeight: 700,
                    }}>
                    {smallTitle}
                  </Typography>
                )}

                <Typography
                  variant='h3'
                  sx={{
                    mb: 2,
                    mt: { xs: 2.5, mobile: 0 },
                    width: smallerTitle === true ? '100%' : '75%',
                    fontWeight: 700,
                    lineHeight: 1.25,
                    color: 'text.primary',
                    fontSize:
                      smallerTitle === true
                        ? { xs: 28, tablet: 36 }
                        : { xs: 24, tablet: 40 },
                  }}>
                  {title}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: { xs: 'none', desktop: 'inline-block' },
                width: '20%',
              }}
            />

            <Box
              sx={{
                width: { xs: '100%', tablet: '50%', desktop: '40%' },
                pl: { xs: 3.5, tablet: 0 },
              }}>
              <Box
                sx={{
                  verticalAlign: 'middle',
                  color: 'primary.light',
                  px: { xs: 3, laptop: 0 },
                }}>
                <PortableText
                  blocks={smalldescription || []}
                  trackSectionHeader={title}
                />

                <Box
                  sx={{
                    display: {
                      xs: 'block',
                      tablet: 'flex',
                      desktop: 'block',
                    },
                    mt: 4,
                  }}>
                  {addbuttons?.map((btn, index) => (
                    <Link
                      key={btn._key}
                      href={btn.btnlink}
                      data-test='middle-banner-button'
                      onClick={e => rudderslabTrackOnClick('button', title, e)}
                      sx={{
                        color: 'text.primary',
                        py: 1.5,
                        mr: index === 0 ? 3 : 0,
                        background: btn.btnhiglight
                          ? 'linear-gradient(135.55deg,#3e35d1 6.09%,#86f8e0 116.03%)'
                          : 'transparent',
                        fontSize: 14,
                        px: { xs: 6, mobile: 5, tablet: 4 },
                        textAlign: 'center',
                        fontWeight: 700,
                        border: `1px solid ${
                          btn.btnhiglight
                            ? 'text.secondary'
                            : palette.primary.light
                        }`,
                        mb: { xs: 2 },
                        justifyContent: 'center',
                        alignItems: 'center',
                        textDecoration: 'none',
                        borderRadius: 2,
                        display: 'inline-block',
                        width: 218,
                        '&:hover': {
                          color: btn.btnhiglight
                            ? 'text.primary'
                            : 'background.default',
                          bgcolor: btn.btnhiglight
                            ? 'background.default'
                            : 'text.primary',
                        },
                      }}>
                      {btn.btntext}
                    </Link>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  )
}
